import { useState, useEffect } from 'react';
import { STORE_INFO } from '@/mocks/content';
import logo from '@/assets/logo.png';

const NAV_LINKS = [
  { label: 'コンセプト', href: '#concept' },
  { label: '店内ツアー', href: '#interior' },
  { label: '料金案内', href: '#pricing' },
  { label: '系列店', href: '#sister' },
  { label: 'アクセス', href: '#access' },
  { label: 'ご予約', href: '#cta' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ドロワーが開いているときはbodyスクロールを止める
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    const el = document.querySelector(href);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#0A0A0F]/95 backdrop-blur-md border-b border-[#C9A84C]/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center cursor-pointer"
          >
            <img
              src={logo}
              alt={`${STORE_INFO.name} logo`}
              className="h-10 md:h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <button
                  onClick={() => handleNavClick(link.href)}
                  className="relative text-white/80 hover:text-[#C9A84C] transition-colors duration-300 text-sm tracking-widest group whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Shippori Mincho', serif" }}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
                </button>
              </li>
            ))}
          </ul>

          {/* Hamburger / Close button */}
          {!drawerOpen && (
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={toggleDrawer}
              aria-label={drawerOpen ? 'メニューを閉じる' : 'メニューを開く'}
            >
              <span className="relative w-6 h-5 flex flex-col justify-between">
                {/* 上の線 */}
                <span
                  className="block w-full h-px bg-[#C9A84C] transition-all duration-300 ease-in-out origin-center"
                  style={{
                    transform: drawerOpen
                      ? 'translateY(10px) rotate(45deg)'
                      : 'translateY(0) rotate(0deg)',
                  }}
                />
                {/* 中央の線 */}
                <span
                  className="block w-full h-px bg-[#C9A84C] transition-all duration-300 ease-in-out"
                  style={{
                    opacity: drawerOpen ? 0 : 1,
                    transform: drawerOpen ? 'scaleX(0)' : 'scaleX(1)',
                  }}
                />
                {/* 下の線 */}
                <span
                  className="block h-px bg-[#C9A84C] transition-all duration-300 ease-in-out origin-center"
                  style={{
                    width: drawerOpen ? '100%' : '66%',
                    alignSelf: 'flex-end',
                    transform: drawerOpen
                      ? 'translateY(-10px) rotate(-45deg)'
                      : 'translateY(0) rotate(0deg)',
                  }}
                />
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* Drawer Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-400 md:hidden ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 bg-[#0D1B2A] border-l border-[#C9A84C]/20 transition-transform duration-500 ease-in-out md:hidden flex flex-col ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#C9A84C]/20">
          <span
            className="text-[#C9A84C] text-lg font-bold tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            {STORE_INFO.name}
          </span>
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={toggleDrawer}
            aria-label={drawerOpen ? 'メニューを閉じる' : 'メニューを開く'}
          >
            <span className="relative w-6 h-5 flex flex-col justify-between">
              {/* 上の線 */}
              <span
                className="block w-full h-px bg-[#C9A84C] transition-all duration-300 ease-in-out origin-center"
                style={{
                  transform: drawerOpen
                    ? 'translateY(10px) rotate(45deg)'
                    : 'translateY(0) rotate(0deg)',
                }}
              />
              {/* 中央の線 */}
              <span
                className="block w-full h-px bg-[#C9A84C] transition-all duration-300 ease-in-out"
                style={{
                  opacity: drawerOpen ? 0 : 1,
                  transform: drawerOpen ? 'scaleX(0)' : 'scaleX(1)',
                }}
              />
              {/* 下の線 */}
              <span
                className="block h-px bg-[#C9A84C] transition-all duration-300 ease-in-out origin-center"
                style={{
                  width: drawerOpen ? '100%' : '66%',
                  alignSelf: 'flex-end',
                  transform: drawerOpen
                    ? 'translateY(-10px) rotate(-45deg)'
                    : 'translateY(0) rotate(0deg)',
                }}
              />
            </span>
          </button>
        </div>

        <ul className="flex flex-col mt-8 px-6 gap-2">
          {NAV_LINKS.map((link, i) => (
            <li key={link.href}>
              <button
                onClick={() => handleNavClick(link.href)}
                className="flex items-center gap-4 w-full py-4 text-white/80 hover:text-[#C9A84C] transition-colors duration-300 cursor-pointer"
                style={{
                  fontFamily: "'Shippori Mincho', serif",
                  transitionDelay: drawerOpen ? `${i * 60}ms` : '0ms',
                }}
              >
                <span className="w-6 h-px bg-[#C9A84C]/60 flex-shrink-0" />
                <span className="tracking-widest text-sm">{link.label}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-auto px-6 pb-10">
          <a
            href={STORE_INFO.telHref}
            className="flex items-center justify-center gap-2 w-full py-3 border border-[#C9A84C]/60 text-[#C9A84C] text-sm tracking-widest hover:bg-[#C9A84C]/10 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            <i className="ri-phone-line" />
            お電話でご予約
          </a>
        </div>
      </div>
    </>
  );
}
